import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpDown, ArrowUp, ArrowDown, ChevronDown } from 'lucide-react';
import { CalculatedMetrics } from '../types';
import { formatVolume, formatPercentage } from '../utils/calculations';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

interface DataTableProps {
  data: CalculatedMetrics[];
}

type SortConfig = {
  key: keyof CalculatedMetrics;
  direction: 'asc' | 'desc';
} | null;

export const DataTable: React.FC<DataTableProps> = ({ data }) => {
  const [sortConfig, setSortConfig] = useState<SortConfig>(null);
  const [visibleRows, setVisibleRows] = useState(20);
  const navigate = useNavigate();

  const handleSort = (key: keyof CalculatedMetrics) => {
    setSortConfig(prevConfig => {
      if (prevConfig?.key === key) {
        return prevConfig.direction === 'asc' 
          ? { key, direction: 'desc' }
          : null;
      }
      return { key, direction: 'asc' };
    });
  };

  const handleTickerClick = (ticker: string) => {
    navigate(`/ticker/${ticker}`);
  };

  const handleShowMore = () => {
    setVisibleRows(prev => prev + 20);
  };

  const sortedData = useMemo(() => {
    if (!sortConfig) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      // Gestione speciale per le date
      if (sortConfig.key === 'Date') {
        const aDate = aVal instanceof Date ? aVal : new Date(aVal as any);
        const bDate = bVal instanceof Date ? bVal : new Date(bVal as any);
        
        // Verifica che entrambe le date siano valide
        const aTime = aDate instanceof Date && !isNaN(aDate.getTime()) ? aDate.getTime() : 0;
        const bTime = bDate instanceof Date && !isNaN(bDate.getTime()) ? bDate.getTime() : 0;
        
        return sortConfig.direction === 'asc' ? aTime - bTime : bTime - aTime;
      }

      // Gestione per valori numerici
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Gestione per stringhe e altri tipi
      const aStr = String(aVal || '').toLowerCase();
      const bStr = String(bVal || '').toLowerCase();
      
      if (sortConfig.direction === 'asc') {
        return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      } else {
        return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
      }
    });
  }, [data, sortConfig]);

  const displayedData = sortedData.slice(0, visibleRows);
  const hasMoreData = visibleRows < sortedData.length;

  const getSortIcon = (key: keyof CalculatedMetrics) => {
    if (sortConfig?.key !== key) {
      return <ArrowUpDown className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-3 w-3 sm:h-4 sm:w-4 text-blue-400" />
      : <ArrowDown className="h-3 w-3 sm:h-4 sm:w-4 text-blue-400" />;
  };

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg overflow-hidden">
      {/* Intestazione Tabella */}
      <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-700">
        <h3 className="text-base sm:text-lg font-semibold text-white">Risultati Analisi Gap</h3>
        <p className="text-xs sm:text-sm text-gray-400 mt-1">
          {displayedData.length} di {sortedData.length} gap mostrati • Clicca sui simboli ticker per l'analisi dettagliata
        </p>
      </div>

      {/* Tabella */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead className="bg-gray-900/50">
            <tr>
              <th className="px-3 sm:px-6 py-3 sm:py-4 text-left">
                <button 
                  onClick={() => handleSort('Date')}
                  className="flex items-center space-x-1 sm:space-x-2 text-xs font-medium text-gray-300 uppercase tracking-wide hover:text-white"
                >
                  <span>Data</span>
                  {getSortIcon('Date')}
                </button>
              </th>
              <th className="px-3 sm:px-6 py-3 sm:py-4 text-left">
                <button 
                  onClick={() => handleSort('Ticker')}
                  className="flex items-center space-x-1 sm:space-x-2 text-xs font-medium text-gray-300 uppercase tracking-wide hover:text-white"
                >
                  <span>Ticker</span>
                  {getSortIcon('Ticker')}
                </button>
              </th>
              <th className="px-3 sm:px-6 py-3 sm:py-4 text-left">
                <button 
                  onClick={() => handleSort('Open')}
                  className="flex items-center space-x-1 sm:space-x-2 text-xs font-medium text-gray-300 uppercase tracking-wide hover:text-white"
                >
                  <span>Apertura</span>
                  {getSortIcon('Open')}
                </button>
              </th>
              <th className="px-3 sm:px-6 py-3 sm:py-4 text-left">
                <button 
                  onClick={() => handleSort('Pre Market Volume')}
                  className="flex items-center space-x-1 sm:space-x-2 text-xs font-medium text-gray-300 uppercase tracking-wide hover:text-white"
                >
                  <span>Volume PM</span>
                  {getSortIcon('Pre Market Volume')}
                </button>
              </th>
              <th className="px-3 sm:px-6 py-3 sm:py-4 text-left">
                <button 
                  onClick={() => handleSort('Volume')}
                  className="flex items-center space-x-1 sm:space-x-2 text-xs font-medium text-gray-300 uppercase tracking-wide hover:text-white"
                >
                  <span>Volume EOD</span>
                  {getSortIcon('Volume')}
                </button>
              </th>
              <th className="px-3 sm:px-6 py-3 sm:py-4 text-left">
                <button 
                  onClick={() => handleSort('Gap %')}
                  className="flex items-center space-x-1 sm:space-x-2 text-xs font-medium text-gray-300 uppercase tracking-wide hover:text-white"
                >
                  <span>Gap %</span>
                  {getSortIcon('Gap %')}
                </button>
              </th>
              <th className="px-3 sm:px-6 py-3 sm:py-4 text-left">
                <button 
                  onClick={() => handleSort('Change %')}
                  className="flex items-center space-x-1 sm:space-x-2 text-xs font-medium text-gray-300 uppercase tracking-wide hover:text-white"
                >
                  <span>Variazione %</span>
                  {getSortIcon('Change %')}
                </button>
              </th>
              <th className="px-3 sm:px-6 py-3 sm:py-4 text-left">
                <button 
                  onClick={() => handleSort('Spike %')}
                  className="flex items-center space-x-1 sm:space-x-2 text-xs font-medium text-gray-300 uppercase tracking-wide hover:text-white"
                >
                  <span>Spike %</span>
                  {getSortIcon('Spike %')}
                </button>
              </th>
              <th className="px-3 sm:px-6 py-3 sm:py-4 text-left">
                <span className="text-xs font-medium text-gray-300 uppercase tracking-wide">Stato</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {displayedData.map((row, index) => (
              <tr key={index} className="hover:bg-gray-800/30 transition-colors">
                <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-white">
                  {format(row.Date, 'dd MMM yyyy', { locale: it })}
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4">
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <button
                      onClick={() => handleTickerClick(row.Ticker)}
                      className="text-xs sm:text-sm font-medium text-white hover:text-blue-400 transition-colors cursor-pointer"
                    >
                      {row.Ticker}
                    </button>
                    <span className={`px-1 sm:px-2 py-1 text-xs rounded-full ${
                      row['Gap %'] > 0 
                        ? 'bg-green-400/10 text-green-400' 
                        : 'bg-red-400/10 text-red-400'
                    }`}>
                      {formatPercentage(row['Gap %'])}
                    </span>
                  </div>
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-300">
                  ${row.Open.toFixed(2)}
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-300">
                  {formatVolume(row['Pre Market Volume'])}
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4">
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <span className="text-xs sm:text-sm text-gray-300">
                      {formatVolume(row.Volume)}
                    </span>
                    <div className="w-12 sm:w-20 h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-400 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${Math.min((row.Volume / row['Pre Market Volume']) * 20, 100)}%` 
                        }}
                      />
                    </div>
                  </div>
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4">
                  <span className={`text-xs sm:text-sm font-medium ${
                    row['Gap %'] > 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {formatPercentage(row['Gap %'])}
                  </span>
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4">
                  <span className={`text-xs sm:text-sm font-medium ${
                    row['Change %'] > 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {formatPercentage(row['Change %'])}
                  </span>
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4">
                  <span className={`text-xs sm:text-sm font-medium ${
                    row['Spike %'] > 0 ? 'text-yellow-400' : 'text-gray-400'
                  }`}>
                    {formatPercentage(row['Spike %'])}
                  </span>
                </td>
                <td className="px-3 sm:px-6 py-3 sm:py-4">
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <div className={`w-2 h-2 rounded-full ${
                      row['Change %'] < 0 ? 'bg-red-400' : 'bg-green-400'
                    }`} />
                    <span className="text-xs text-gray-400">
                      {row['Change %'] < 0 ? 'ROSSO' : 'VERDE'}
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pulsante Mostra Altro */}
      {hasMoreData && (
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-700 text-center">
          <button
            onClick={handleShowMore}
            className="flex items-center justify-center space-x-2 mx-auto px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors text-sm"
          >
            <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Mostra altri 20 risultati</span>
            <span className="text-xs sm:text-sm text-blue-200">
              ({sortedData.length - visibleRows} rimanenti)
            </span>
          </button>
        </div>
      )}
    </div>
  );
};